/** Where a settlement record stands between the partner who books hours and the host who answers. */
export type SettlementRecordState = "open" | "submitted" | "reviewed" | "approved";

/** The three facts about a settlement record its state is read from. */
export interface GetSettlementRecordStateInput {
	isApproved: boolean;
	isReviewed: boolean;
	hasBookings: boolean;
}

/**
 * Read a settlement record's state from its approval, its review and whether it holds bookings,
 * so both sides of a partnership name the same record the same way.
 *
 * - `open`: nobody has booked hours yet — the partner still owes them, the host has nothing to
 *   answer.
 * - `submitted`: the partner has booked hours and the host has not answered them — the partner
 *   waits, the host has a review to do.
 * - `reviewed`: the host has answered the bookings but not approved the record — the partner
 *   sees the corrections, the host still has "Genehmigen" ahead.
 * - `approved`: the host has closed the record — the hours are settled and no side writes more.
 *
 * Approval wins over everything, since an approved record is closed whatever else it holds. A
 * review stamp without bookings reads as `open`, because there are no hours it could have
 * answered.
 *
 * @param input - The record's approval, review and whether it holds any booking.
 * @returns The record's state.
 */
export function getSettlementRecordState({
	isApproved,
	isReviewed,
	hasBookings,
}: GetSettlementRecordStateInput): SettlementRecordState {
	if (isApproved) return "approved";
	if (!hasBookings) return "open";
	if (isReviewed) return "reviewed";
	return "submitted";
}
